/**
 * Web adapter — sql.js (WASM SQLite) persisted to durable per-origin storage
 * so PWA installs survive reloads and offline restarts.
 *
 * Persistence model:
 *   - One `Database` instance is held in module memory.
 *   - A backend is picked once per open() (see `resolvePersistBackend`):
 *     OPFS where it is actually writable, else IndexedDB, else in-memory.
 *     Older iOS/iPadOS Safari (< 18) has no main-thread OPFS write path, so
 *     it lands on IndexedDB instead of throwing on every flush.
 *   - On open: bytes are loaded from the chosen backend (if present) and fed
 *     to `new SQL.Database(bytes)`.
 *   - On every successful `exec`/`transaction`: we schedule a debounced
 *     `flush()` that writes `db.export()` back via the backend (OPFS writes
 *     atomically to `{name}.tmp` → swap).
 *   - On `close()`: cancel debounce + final flush, then `db.close()`.
 *
 * Concurrency: sql.js is synchronous; we never `await` between `BEGIN` and
 * `COMMIT`, so transactions are atomic w.r.t. a single tab. Multi-tab is NOT
 * supported (sql.js + OPFS is single-writer). The app gates open behind a
 * `navigator.locks.request('coach.db', …)` so a second tab waits cleanly.
 */
import type { LocalDb, LocalDbFactory, LocalDbRow, SqlValue } from "../local-db.js";

type SqlJsStatic = {
  Database: new (data?: Uint8Array) => SqlJsDatabase;
};
type SqlJsDatabase = {
  run(sql: string, params?: unknown[]): void;
  exec(sql: string): Array<{ columns: string[]; values: unknown[][] }>;
  prepare(sql: string): SqlJsStatement;
  export(): Uint8Array;
  close(): void;
  getRowsModified(): number;
};
type SqlJsStatement = {
  bind(params?: unknown[]): boolean;
  step(): boolean;
  getAsObject(): Record<string, unknown>;
  free(): void;
  run(params?: unknown[]): void;
};

const FLUSH_DEBOUNCE_MS = 250;

/**
 * Apps can pre-resolve the wasm URL via Vite's `?url` import so we don't have
 * to fetch a fixed path that a stale service worker might intercept. Set this
 * before calling `webLocalDb.open()` — typically in `main.tsx`:
 *
 *   import sqlWasmUrl from 'sql.js/dist/sql-wasm.wasm?url';
 *   configureWebSqlWasm(sqlWasmUrl);
 *
 * When unset we fall back to a fixed `/sql-wasm.wasm` path served from
 * `apps/app/public/`.
 */
let configuredWasmUrl: string | null = null;
export function configureWebSqlWasm(url: string): void {
  configuredWasmUrl = url;
}

async function loadSqlJs(): Promise<SqlJsStatic> {
  // Dynamic import so Tauri builds never pull the wasm.
  const mod = (await import("sql.js")) as unknown as {
    default: (config?: { locateFile?: (file: string) => string }) => Promise<SqlJsStatic>;
  };
  const initSqlJs = mod.default;
  return initSqlJs({
    locateFile: (file: string) => {
      // Prefer the bundler-resolved URL (cache-busting hash + outside SW
      // navigateFallback's reach). Falls back to a fixed root path.
      if (configuredWasmUrl && file.endsWith(".wasm")) return configuredWasmUrl;
      return `/${file}`;
    },
  });
}

async function readOpfs(name: string): Promise<Uint8Array | null> {
  if (typeof navigator === "undefined" || !navigator.storage?.getDirectory) return null;
  try {
    const root = await navigator.storage.getDirectory();
    const fh = await root.getFileHandle(name, { create: false });
    const file = await fh.getFile();
    const buf = await file.arrayBuffer();
    return new Uint8Array(buf);
  } catch {
    return null;
  }
}

async function writeOpfsAtomic(name: string, bytes: Uint8Array): Promise<void> {
  if (typeof navigator === "undefined" || !navigator.storage?.getDirectory) return;
  const root = await navigator.storage.getDirectory();
  const tmpName = `${name}.tmp`;
  const tmp = await root.getFileHandle(tmpName, { create: true });
  // FileSystemSyncAccessHandle is the fast path (workers + recent browsers);
  // fall back to createWritable() in the main thread.
  // @ts-expect-error - createSyncAccessHandle exists on FileSystemFileHandle in worker contexts.
  if (typeof tmp.createSyncAccessHandle === "function") {
    // @ts-expect-error - see above
    const handle = await tmp.createSyncAccessHandle();
    try {
      handle.truncate(0);
      handle.write(bytes, { at: 0 });
      handle.flush();
    } finally {
      handle.close();
    }
  } else {
    const writable = await tmp.createWritable();
    // Cast around lib.dom's new Uint8Array<ArrayBufferLike> vs FileSystemWriteChunkType
    // mismatch — the runtime accepts any BufferSource.
    await writable.write(bytes as unknown as BufferSource);
    await writable.close();
  }
  // Atomic-ish swap: OPFS has no rename, so we delete + recreate from tmp.
  try {
    await root.removeEntry(name);
  } catch {
    /* file may not exist yet */
  }
  // Re-create the canonical file by copying tmp into it.
  const final = await root.getFileHandle(name, { create: true });
  const writable = await final.createWritable();
  const tmpFile = await tmp.getFile();
  await writable.write(await tmpFile.arrayBuffer());
  await writable.close();
  try {
    await root.removeEntry(tmpName);
  } catch {
    /* best-effort cleanup */
  }
}

/* ─── persistence backend selection ──────────────────────────────────────
 * The exported SQLite bytes must land in durable per-origin storage. OPFS is
 * the fast path, but *writing* it needs either FileSystemSyncAccessHandle
 * (worker-only) or FileSystemFileHandle.createWritable() — and WebKit only
 * shipped createWritable() in Safari 18. On older iOS/iPadOS there is NO
 * main-thread OPFS write path, so `writeOpfsAtomic` throws on every flush.
 * Combined with the full synchronous `db.export()` each flush performs, those
 * repeated failures saturate the main thread for minutes (the "page renders
 * half, then the rest 5 min later" hang). We therefore probe once at open():
 * if OPFS can't be written we fall back to IndexedDB, which every Safari
 * supports. Capable browsers keep the identical OPFS path they always used. */

interface PersistBackend {
  label: "opfs" | "idb" | "none";
  read(name: string): Promise<Uint8Array | null>;
  write(name: string, bytes: Uint8Array): Promise<void>;
}

/** True iff a real OPFS write succeeds on this device/thread right now. */
async function opfsCanWrite(): Promise<boolean> {
  if (typeof navigator === "undefined" || !navigator.storage?.getDirectory) return false;
  const probe = "__coach_opfs_probe__";
  let ok = false;
  try {
    await writeOpfsAtomic(probe, new Uint8Array([1]));
    ok = true;
  } catch {
    ok = false;
  }
  // Best-effort cleanup of the probe artefacts either way.
  try {
    const root = await navigator.storage.getDirectory();
    await root.removeEntry(probe).catch(() => {});
    await root.removeEntry(`${probe}.tmp`).catch(() => {});
  } catch {
    /* ignore */
  }
  return ok;
}

/* ─── IndexedDB fallback ─────────────────────────────────────────────── */

const IDB_NAME = "coach-localdb";
const IDB_STORE = "sqlite";

function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexedDB.open failed"));
    req.onblocked = () => reject(new Error("indexedDB.open blocked"));
  });
}

/** Normalise whatever IndexedDB returns (Uint8Array | ArrayBuffer | view). */
export function toUint8(value: unknown): Uint8Array | null {
  if (value == null) return null;
  if (value instanceof Uint8Array) return value;
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  if (ArrayBuffer.isView(value)) {
    const v = value as ArrayBufferView;
    return new Uint8Array(v.buffer, v.byteOffset, v.byteLength);
  }
  return null;
}

function makeIdbBackend(): PersistBackend {
  let conn: Promise<IDBDatabase> | null = null;
  const db = (): Promise<IDBDatabase> => {
    conn ??= idbOpen();
    return conn;
  };
  return {
    label: "idb",
    async read(name) {
      const database = await db();
      return new Promise<Uint8Array | null>((resolve, reject) => {
        const tx = database.transaction(IDB_STORE, "readonly");
        const req = tx.objectStore(IDB_STORE).get(name);
        req.onsuccess = () => resolve(toUint8(req.result));
        req.onerror = () => reject(req.error ?? new Error("idb get failed"));
      });
    },
    async write(name, bytes) {
      const database = await db();
      // Copy so we never hand a SharedArrayBuffer-backed view to structured
      // clone (it rejects those) and so a later mutation of sql.js's export
      // buffer can't corrupt the stored value.
      const copy = new Uint8Array(bytes.byteLength);
      copy.set(bytes);
      return new Promise<void>((resolve, reject) => {
        const tx = database.transaction(IDB_STORE, "readwrite");
        tx.objectStore(IDB_STORE).put(copy, name);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error("idb put failed"));
        tx.onabort = () => reject(tx.error ?? new Error("idb put aborted"));
      });
    },
  };
}

/** Pure backend choice — extracted so it can be unit-tested without browsers. */
export function pickPersistBackendLabel(
  opfsWritable: boolean,
  hasIndexedDb: boolean,
): "opfs" | "idb" | "none" {
  if (opfsWritable) return "opfs";
  if (hasIndexedDb) return "idb";
  return "none";
}

async function resolvePersistBackend(): Promise<PersistBackend> {
  const label = pickPersistBackendLabel(await opfsCanWrite(), typeof indexedDB !== "undefined");
  if (label === "opfs") return { label, read: readOpfs, write: writeOpfsAtomic };
  if (label === "idb") return makeIdbBackend();
  // No durable storage at all — run purely in-memory. Data reloads from the
  // server on next launch; far better than freezing on every failed flush.
  return { label: "none", read: async () => null, write: async () => {} };
}

export const webLocalDb: LocalDbFactory = {
  async open(name) {
    if (typeof window === "undefined") {
      throw new Error("webLocalDb requires a browser environment");
    }

    const SQL = await loadSqlJs();
    const backend = await resolvePersistBackend();
    // biome-ignore lint/suspicious/noConsole: one-time boot diagnostic (which storage won)
    console.info(`[webLocalDb] persistence backend: ${backend.label}`);
    const existing = await backend.read(name);
    const raw: SqlJsDatabase = new SQL.Database(existing ?? undefined);

    let dirty = false;
    let flushTimer: ReturnType<typeof setTimeout> | null = null;
    let flushing: Promise<void> | null = null;
    let closed = false;

    async function flush(): Promise<void> {
      if (closed) return;
      if (!dirty) return;
      if (flushing) return flushing;
      flushing = (async () => {
        const snapshot = raw.export();
        dirty = false;
        try {
          await backend.write(name, snapshot);
        } catch (err) {
          // If we fail to persist, mark dirty again so the next flush retries.
          dirty = true;
          // biome-ignore lint/suspicious/noConsole: persistence failures must surface
          console.error(`[webLocalDb] ${backend.label} flush failed`, err);
        } finally {
          flushing = null;
        }
      })();
      return flushing;
    }

    function scheduleFlush(): void {
      dirty = true;
      if (flushTimer) clearTimeout(flushTimer);
      flushTimer = setTimeout(() => {
        void flush();
      }, FLUSH_DEBOUNCE_MS);
    }

    function runSync(sql: string, params: SqlValue[] = []): { rowsAffected: number } {
      if (params.length === 0) {
        raw.run(sql);
      } else {
        const stmt = raw.prepare(sql);
        try {
          stmt.run(params as unknown[]);
        } finally {
          stmt.free();
        }
      }
      return { rowsAffected: raw.getRowsModified() };
    }

    function querySync<T extends LocalDbRow>(sql: string, params: SqlValue[] = []): T[] {
      const stmt = raw.prepare(sql);
      const out: T[] = [];
      try {
        stmt.bind(params as unknown[]);
        while (stmt.step()) {
          out.push(stmt.getAsObject() as unknown as T);
        }
      } finally {
        stmt.free();
      }
      return out;
    }

    const wrap = (inTransaction: boolean): LocalDb => ({
      async exec(sql, params = []) {
        const r = runSync(sql, params);
        if (!inTransaction) scheduleFlush();
        return r;
      },
      async query<T extends LocalDbRow = LocalDbRow>(sql: string, params: SqlValue[] = []) {
        return querySync<T>(sql, params);
      },
      async transaction(fn) {
        // sql.js doesn't support nested SAVEPOINTs through our wrapper today;
        // serialize nested calls by reusing the outer transaction.
        if (inTransaction) return fn(wrap(true));
        runSync("BEGIN");
        try {
          const result = await fn(wrap(true));
          runSync("COMMIT");
          scheduleFlush();
          return result;
        } catch (err) {
          try {
            runSync("ROLLBACK");
          } catch {
            /* best-effort */
          }
          throw err;
        }
      },
      async close() {
        if (closed) return;
        closed = true;
        if (flushTimer) {
          clearTimeout(flushTimer);
          flushTimer = null;
        }
        // Final synchronous flush so we don't lose the last write.
        if (dirty) {
          dirty = false;
          try {
            const snapshot = raw.export();
            await backend.write(name, snapshot);
          } catch (err) {
            // biome-ignore lint/suspicious/noConsole: final flush failure is critical
            console.error(`[webLocalDb] ${backend.label} final flush failed`, err);
          }
        }
        raw.close();
      },
    });

    return wrap(false);
  },
};
